import { Check, SlidersHorizontal, X } from 'lucide-react';
import { ModalDialog } from '@/components/ModalDialog';
import { THEME_PALETTES, useTheme } from '@/lib/theme';
import { cn } from '@/lib/utils';

const LABEL_ID = 'mau-giao-dien';

/**
 * The HyperTek prototype's theme drawer (h-prototype `components/layout/theme-drawer.tsx`): a 320 px sheet
 * on the right with an icon-tile header, a three-column grid of round swatches with a tick badge on the
 * chosen one, and a preview of the two button styles in the chosen colour.
 *
 * Two things differ from the prototype, both on purpose. It is built on ModalDialog (focus in, Tab kept
 * inside, Escape and focus back to the opener) rather than on a fixed `div` or Radix Dialog, which would
 * write a `<style>` element the production CSP refuses. And the swatches are native radios inside labels, so
 * arrow keys move between them and a screen reader announces "1 of 9"; the preview is decoration and is
 * hidden from assistive technology, so it adds no dead controls to the Tab order. The swatch colour is a
 * React `style` prop (CSSOM, allowed).
 */
export function ThemeDrawer({ onClose }: { onClose: () => void }) {
  const { theme, setTheme } = useTheme();
  return (
    <ModalDialog labelId={LABEL_ID} onClose={onClose} variant="sheet" className="max-w-80 p-0 sm:p-0">
      <div className="flex items-center justify-between border-b border-[hsl(var(--h-border))] p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--h-primary))]/10">
            <SlidersHorizontal className="h-5 w-5 text-[hsl(var(--h-primary))]" aria-hidden="true" />
          </div>
          <div>
            <h2 id={LABEL_ID} className="font-semibold text-[hsl(var(--h-card-foreground))]">Màu giao diện</h2>
            <p className="text-xs text-[hsl(var(--h-muted-foreground))]">Lựa chọn chỉ lưu trên trình duyệt này.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-2 transition-colors hover:bg-[hsl(var(--h-muted))]"
        >
          <X className="h-5 w-5 text-[hsl(var(--h-muted-foreground))]" aria-hidden="true" />
          <span className="sr-only">Đóng</span>
        </button>
      </div>

      <div className="p-5">
        <fieldset>
          <legend className="mb-4 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--h-muted-foreground))]">
            Màu chủ đạo
          </legend>
          <div className="grid grid-cols-3 gap-3">
            {THEME_PALETTES.map((palette) => {
              const chosen = palette.id === theme.id;
              return (
                <label
                  key={palette.id}
                  className={cn(
                    'relative flex cursor-pointer flex-col items-center gap-2 rounded-lg border p-3 transition-all hover:shadow-md',
                    'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[hsl(var(--h-primary))] has-[:focus-visible]:ring-offset-2',
                    chosen
                      ? 'border-[hsl(var(--h-primary))] bg-[hsl(var(--h-primary))]/5 shadow-md'
                      : 'border-[hsl(var(--h-border))] hover:border-[hsl(var(--h-muted-foreground))]/30',
                  )}
                >
                  <input
                    type="radio" name="mau-chu-dao" value={palette.id} checked={chosen}
                    onChange={() => setTheme(palette)} className="sr-only"
                  />
                  <span
                    aria-hidden="true"
                    className="h-10 w-10 rounded-full shadow-md ring-2 ring-white"
                    style={{ backgroundColor: `hsl(${palette.primary})` }}
                  />
                  <span className="text-center text-xs font-medium text-[hsl(var(--h-card-foreground))]">
                    {palette.name}
                  </span>
                  {chosen && (
                    <span
                      aria-hidden="true"
                      className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--h-primary))] shadow-md"
                    >
                      <Check className="h-3 w-3 text-white" />
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </fieldset>

        <div aria-hidden="true" className="mt-6 border-t border-[hsl(var(--h-border))] pt-5">
          <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--h-muted-foreground))]">
            Xem trước
          </p>
          <div className="flex gap-3">
            <span className="flex-1 rounded-lg bg-[hsl(var(--h-primary))] py-2.5 text-center text-sm font-semibold text-white shadow-md">
              Nút chính
            </span>
            <span className="flex-1 rounded-lg bg-[hsl(var(--h-primary))]/10 py-2.5 text-center text-sm font-semibold text-[hsl(var(--h-primary))]">
              Nút phụ
            </span>
          </div>
        </div>
      </div>
    </ModalDialog>
  );
}
